import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { SquadshotService } from '../../services/squadshot/squadshot.service';
import { DolbyService } from '../../services/dolby/dolby.service';
import { setCssVar } from '../../util/setCssVar';
import { DailyService } from '../../services/daily/daily.service';

@Component({
  selector: 'sc-canvas',
  templateUrl: './canvas.component.html',
  styleUrls: ['./canvas.component.scss'],
})
export class CanvasComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('canvas') canvas: ElementRef;

  @Input() minAspectRatio: number = 8 / 9;
  @Input() maxAspectRatio: number = 16 / 9;
  @Input() gap = 8;
  @Input() borderRadius = 5;
  @Input() fit: 'cover' | 'contain' | ('cover' | 'contain')[] = 'cover';
  @Input() moreFPS = false;

  dolbyConversation$ = this.dolbyService.dolbyConversation$;
  dailyCall$ = this.dailyService.dailyCall$;

  lastUpdate = 0;
  resizeObserver: ResizeObserver;
  subs: Array<Subscription> = [];

  constructor(
    private host: ElementRef,
    private dolbyService: DolbyService,
    private dailyService: DailyService,
    private squadshotService: SquadshotService
  ) {}

  ngOnInit() {
    this.watchResize(this.host.nativeElement);
  }

  ngAfterViewInit() {
    this.watchParticipants();
  }

  watchResize(elm: HTMLElement) {
    this.resizeObserver = new ResizeObserver(() => {
      this.calculatePositioning(this.canvas.nativeElement);
    });
    this.resizeObserver.observe(elm);
  }

  watchParticipants() {
    if (this.dolbyConversation$?.value) {
      const dolbySub = this.dolbyService.stageParticipants$.subscribe(() => {
        setTimeout(() => {
          this.calculatePositioning(this.canvas.nativeElement);
        }, 0);
      });
      this.subs.push(dolbySub);
    }

    if (this.dailyCall$?.value) {
      const dailySub = this.dailyService.stageParticipants$.subscribe(() => {
        setTimeout(() => {
          this.calculatePositioning(this.canvas.nativeElement);
        }, 0);
      });
      this.subs.push(dailySub);
    }

    const squadshotRenderSub = this.squadshotService.squadshotRenderReady$.subscribe((ready) => {
      if (ready) this.calculatePositioning(this.canvas.nativeElement);
    });
    this.subs.push(squadshotRenderSub);
  }

  calculatePositioning(elm: HTMLElement) {
    const parentCS = getComputedStyle(elm.parentElement);
    const elmW = elm.parentElement.clientWidth - parseFloat(parentCS.paddingTop) - parseFloat(parentCS.paddingBottom);
    const elmH = elm.parentElement.clientHeight - parseFloat(parentCS.paddingLeft) - parseFloat(parentCS.paddingRight);
    setCssVar(elm, 'width', `${elmW}px`);
    setCssVar(elm, 'height', `${elmH}px`);
    if (elmW - this.gap <= 0 || elmH - this.gap <= 0) return;
    const childrenCount = elm.childElementCount;

    let rowCount = 0;
    let elmClusterWidth = Number.POSITIVE_INFINITY;
    const rowElmCount = () => {
      return Math.ceil(childrenCount / rowCount);
    };
    const childH = () => {
      return elmH / rowCount;
    };
    const minClusterWidth = () => {
      return rowElmCount() * childH() * this.minAspectRatio + this.gap * rowElmCount();
    };
    const maxClusterWidth = () => {
      return rowElmCount() * childH() * this.maxAspectRatio + this.gap * rowElmCount();
    };

    while (elmClusterWidth > elmW) {
      rowCount++;
      elmClusterWidth = minClusterWidth();
    }

    const width = maxClusterWidth() < elmW ? childH() * this.maxAspectRatio : elmW / rowElmCount() - this.gap;

    setCssVar(elm, 'child-width', `${width}px`);
    setCssVar(elm, 'child-height', `${childH() - this.gap}px`);
    setCssVar(elm, 'child-margin', `${this.gap / 2}px`);
    setCssVar(elm, 'child-border-radius', `${this.borderRadius}px`);
    setCssVar(elm, 'padding-horizontal', `${(elmW - maxClusterWidth()) / 2}px`);

    if (!Array.isArray(this.fit)) setCssVar(elm, 'child-object-fit', this.fit);
    if (Array.isArray(this.fit)) {
      for (let i = 0; i < elm.children.length; i++) {
        const child = elm.children[i] as HTMLElement;
        if (this.fit[i]) {
          child.querySelectorAll('video').forEach((item) => (item.style.objectFit = this.fit[i]));
        }
      }
    }
  }

  ngOnDestroy() {
    this.resizeObserver.disconnect();
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
